import config from 'config/config';
import { api } from 'redux/api/index.api';
import checkOrSetSlash from 'utils/checkOrSetSlash';
import { getBusinessOrganizationVersion } from 'utils/constants';

const rootAdminPath = `${checkOrSetSlash(config.apiHostGatewayGlobal, 'apiHostGatewayGlobal')}api/`;

export const superAdminOrganizationsApi = api.injectEndpoints({
  endpoints: builder => ({
    fetchOrganizations: builder.query({
      query: ({ searchValue = '', limit, offset }) => ({
        url: `${rootAdminPath}${getBusinessOrganizationVersion()}/admin/organizations/filters`,
        method: 'POST',
        params: {
          limit,
          offset,
        },
        body: { search_term: searchValue },
      }),
      providesTags: ['OrganizationsList'],
      transformResponse: response => ({
        organizations: response.organizations || [],
        total: response.count || 0,
      }),
    }),
    fetchOffers: builder.query({
      query: () => ({
        url: `${rootAdminPath}offers`,
        method: 'GET',
      }),
      providesTags: ['Offers'],
      transformResponse: response => response?.offers || [],
    }),
    fetchContractInfo: builder.query({
      query: subscriptionId => ({
        url: `${rootAdminPath}subscriptions/${subscriptionId}`,
        method: 'GET',
      }),
      providesTags: (result, error, subscriptionId) => [
        { type: 'ContractInfo', id: subscriptionId },
      ],
    }),
    fetchDeletedOrganizations: builder.query({
      query: ({ searchValue = '', limit, offset }) => ({
        url: `${rootAdminPath}${getBusinessOrganizationVersion()}/admin/organizations/deleted`,
        method: 'POST',
        params: {
          limit,
          offset,
        },
        body: { search_term: searchValue },
      }),
      providesTags: ['DeletedOrganizationsList'],
      transformResponse: response => ({
        organizations: response.organizations || [],
        total: response.count || 0,
      }),
    }),
    setSalesforceReference: builder.mutation({
      query: ({ id, salesforce_reference }) => ({
        url: `${getBusinessOrganizationVersion()}/organizations/${id}/set-reference`,
        method: 'POST',
        body: { salesforce_reference },
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'Organization', id },
        'OrganizationsList',
      ],
    }),
    updateSubscription: builder.mutation({
      query: ({ subscription_id, data }) => ({
        url: `${rootAdminPath}subscriptions/${subscription_id}`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: ['OrganizationsList', { type: 'ContractInfo', id: undefined }],
    }),
    updateProjectCreationLimitation: builder.mutation({
      query: ({ businessOrganizationId, projectCreationLimited }) => ({
        url: `${rootAdminPath}${getBusinessOrganizationVersion()}/admin/organizations/${businessOrganizationId}/set-project-creation-limitation`,
        method: 'PATCH',
        body: {
          is_limited: projectCreationLimited,
        },
      }),
      invalidatesTags: ['OrganizationsList'],
    }),
    changeBusinessOrganizationPlan: builder.mutation({
      query: ({ businessOrganizationId, offerId }) => ({
        url: `${rootAdminPath}offers/${businessOrganizationId}/change_offer`,
        method: 'POST',
        body: { offer_id: offerId },
      }),
      invalidatesTags: ['Offers', 'OrganizationsList'],
    }),
    reactivateDeletedOrganization: builder.mutation({
      query: ({ id }) => ({
        url: `${rootAdminPath}${getBusinessOrganizationVersion()}/admin/organizations/${id}/reactivate`,
        method: 'POST',
      }),
      invalidatesTags: ['DeletedOrganizationsList'],
    }),
  }),
  overrideExisting: 'throw',
});

export const {
  useFetchOrganizationsQuery,
  useFetchOffersQuery,
  useFetchContractInfoQuery,
  useFetchDeletedOrganizationsQuery,
  useSetSalesforceReferenceMutation,
  useUpdateSubscriptionMutation,
  useUpdateProjectCreationLimitationMutation,
  useChangeBusinessOrganizationPlanMutation,
  useReactivateDeletedOrganizationMutation,
} = superAdminOrganizationsApi;
